import { useMemo } from 'react';
import { Group } from '@visx/group';
import { useParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { valueAt } from '../../SensorTimeline/lib/hover';
import {
  leftMarginFor,
  PLOT_HEIGHT,
  Y_LABEL_GAP,
  yAxisFor,
  type LinearScale,
} from '../../SensorTimeline/lib/layout';
import type { DeviceLine } from '../../SensorTimeline/lib/chartModel';
import { useCrosshair } from '../../SensorTimeline/useCrosshair';
import { useLabelMeasure } from '../../SensorTimeline/useLabelMeasure';
import GxpChartTooltip from './GxpChartTooltip';
import {
  elapsedTicks,
  temperatureProfile,
  type TemperatureProfile,
  type TemperatureSeries,
} from './temperatureProfile';
import { GxpExcursionEvent } from './types';

const MARGIN = { top: 20, right: 8, bottom: 24 };
const GAP_LABEL_MIN_PX = 48;
const PEAK_LABEL_OFFSET_PX = 10;
const LIMIT_LABEL_CLEARANCE_PX = 96;
const NO_EXCURSIONS: GxpExcursionEvent[] = [];

interface TemperatureProfileChartProps {
  /** The temperature-series endpoint's response. */
  chartApiData: TemperatureSeries | null | undefined;
  excursionEvents?: GxpExcursionEvent[];
}

const indexesOf = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

const SeriesLayer = ({
  lines,
  xScale,
  yScale,
}: {
  lines: readonly DeviceLine[];
  xScale: LinearScale;
  yScale: LinearScale;
}) => (
  <>
    {lines.map(({ device, t, v, segments }) =>
      segments.map(([start, end]) =>
        start === end ? (
          <circle
            key={`${device.id}-${t[start]}`}
            cx={xScale(t[start])}
            cy={yScale(v[start])}
            r={2.5}
            fill={device.color}
          />
        ) : (
          <LinePath<number>
            key={`${device.id}-${t[start]}`}
            data={indexesOf(start, end)}
            x={(i) => xScale(t[i])}
            y={(i) => yScale(v[i])}
            stroke={device.color}
            strokeWidth={2}
          />
        )
      )
    )}
  </>
);

/**
 * The plot at its measured width: the in-range band between the limits with out-of-range above and below, "no data"
 * gaps, each excursion's interval with a pin at its peak, POP and POD markers, a line per sensor, and hours since POP
 * along the x axis (PRD 4.1 Temperature profile chart; Figma). Hover reads each sensor's value against the limits.
 */
const SizedChart = ({
  profile,
  width,
}: {
  profile: TemperatureProfile;
  width: number;
}) => {
  const {
    model,
    window,
    pop,
    pod,
    limits,
    sensorLimits,
    gaps,
    excursions,
    devices,
  } = profile;
  const measure = useLabelMeasure();
  const yAxis = useMemo(() => yAxisFor(model.domain, ''), [model.domain]);
  const marginLeft = leftMarginFor([yAxis], measure);
  const plotRight = width - MARGIN.right;
  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [window[0], window[1]],
        range: [marginLeft, plotRight],
      }),
    [window, marginLeft, plotRight]
  );
  const xTicks = useMemo(
    () =>
      elapsedTicks(pop ?? window[0], window, plotRight - marginLeft, measure),
    [pop, window, plotRight, marginLeft, measure]
  );
  const models = useMemo(() => [model], [model]);
  const { hover, move, leave } = useCrosshair(models, xScale);
  const yScale = yAxis.scale;
  const height = MARGIN.top + PLOT_HEIGHT + MARGIN.bottom;
  const plotWidth = Math.max(0, plotRight - marginLeft);
  const spanX = (from: number, to: number) => {
    const x = Math.max(marginLeft, xScale(from));
    return { x, width: Math.max(0, Math.min(plotRight, xScale(to)) - x) };
  };
  const limitLabelsLeft = excursions.some(
    (mark) =>
      mark.peakAt !== null &&
      xScale(mark.peakAt) > plotRight - LIMIT_LABEL_CLEARANCE_PX
  );
  const custody = [
    { label: 'POP', at: pop, anchor: 'start' as const },
    { label: 'POD', at: pod, anchor: 'end' as const },
  ].filter(
    (mark): mark is { label: string; at: number; anchor: 'start' | 'end' } =>
      mark.at !== null && mark.at >= window[0] && mark.at <= window[1]
  );

  return (
    <>
      <svg
        width={width}
        height={height}
        role='img'
        aria-label='Temperature profile'
        className='gxp-profile'
        onPointerMove={move}
        onPointerDown={move}
        onPointerLeave={leave}
      >
        <Group top={MARGIN.top}>
          {limits && (
            <>
              <rect
                className='gxp-profile__out-of-range'
                x={marginLeft}
                width={plotWidth}
                y={0}
                height={Math.max(0, yScale(limits[1]))}
              />
              <rect
                className='gxp-profile__in-range'
                x={marginLeft}
                width={plotWidth}
                y={yScale(limits[1])}
                height={Math.max(0, yScale(limits[0]) - yScale(limits[1]))}
              />
              <rect
                className='gxp-profile__out-of-range'
                x={marginLeft}
                width={plotWidth}
                y={yScale(limits[0])}
                height={Math.max(0, PLOT_HEIGHT - yScale(limits[0]))}
              />
            </>
          )}
          {excursions.map((mark) => (
            <rect
              key={`band-${mark.key}`}
              className='gxp-profile__excursion'
              data-testid='excursion-band'
              {...spanX(mark.from, mark.to)}
              y={0}
              height={PLOT_HEIGHT}
            />
          ))}
          {yAxis.ticks.map((tick) => (
            <text
              key={tick.value}
              className='gxp-profile__axis-label'
              x={marginLeft - Y_LABEL_GAP}
              y={yScale(tick.value)}
              textAnchor='end'
              dominantBaseline='middle'
            >
              {tick.label}
            </text>
          ))}
          {limits &&
            [limits[1], limits[0]].map((value) => (
              <line
                key={`limit-${value}`}
                className='gxp-profile__limit'
                x1={marginLeft}
                x2={plotRight}
                y1={yScale(value)}
                y2={yScale(value)}
              />
            ))}
          {gaps.map(([from, to]) => {
            const span = spanX(from, to);
            return (
              <g key={`gap-${from}-${to}`} data-testid='gap'>
                <rect
                  className='gxp-profile__gap'
                  {...span}
                  y={0}
                  height={PLOT_HEIGHT}
                />
                {span.width >= GAP_LABEL_MIN_PX && (
                  <text
                    className='gxp-profile__gap-label'
                    x={span.x + span.width / 2}
                    y={PLOT_HEIGHT / 2}
                    textAnchor='middle'
                    dominantBaseline='middle'
                  >
                    No data
                  </text>
                )}
              </g>
            );
          })}
          {limits &&
            [
              { value: limits[1], label: `max ${limits[1]}°C`, dy: -4 },
              { value: limits[0], label: `min ${limits[0]}°C`, dy: 12 },
            ].map(({ value, label, dy }) => (
              <text
                key={label}
                className='gxp-profile__limit-label'
                x={limitLabelsLeft ? marginLeft + 4 : plotRight - 4}
                y={yScale(value) + dy}
                textAnchor={limitLabelsLeft ? 'start' : 'end'}
              >
                {label}
              </text>
            ))}
          <SeriesLayer lines={model.lines} xScale={xScale} yScale={yScale} />
          {custody.map(({ label, at, anchor }) => (
            <g key={label} data-testid='custody-marker'>
              <line
                className='gxp-profile__custody'
                x1={xScale(at)}
                x2={xScale(at)}
                y1={0}
                y2={PLOT_HEIGHT}
              />
              <text
                className='gxp-profile__custody-label'
                x={xScale(at) + (anchor === 'start' ? 2 : -2)}
                y={-6}
                textAnchor={anchor}
              >
                {label}
              </text>
            </g>
          ))}
          {excursions.flatMap((mark) =>
            mark.peakAt === null || mark.peak === null
              ? []
              : [
                  <g key={`peak-${mark.key}`} data-testid='excursion-peak'>
                    <circle
                      className='gxp-profile__peak'
                      cx={xScale(mark.peakAt)}
                      cy={yScale(mark.peak)}
                      r={4}
                    />
                    <text
                      className='gxp-profile__peak-label'
                      x={xScale(mark.peakAt)}
                      y={
                        yScale(mark.peak) +
                        (mark.below
                          ? PEAK_LABEL_OFFSET_PX + 8
                          : -PEAK_LABEL_OFFSET_PX)
                      }
                      textAnchor='middle'
                    >
                      {`${mark.peak}°C`}
                    </text>
                  </g>,
                ]
          )}
          {hover && (
            <>
              <line
                className='gxp-profile__crosshair'
                x1={xScale(hover.at)}
                x2={xScale(hover.at)}
                y1={0}
                y2={PLOT_HEIGHT}
              />
              {model.lines.flatMap((line) => {
                const value = valueAt(line, hover.at, hover.tolerance);
                return value === null
                  ? []
                  : [
                      <circle
                        key={line.device.id}
                        cx={xScale(hover.at)}
                        cy={yScale(value)}
                        r={3.5}
                        fill={line.device.color}
                        className='gxp-profile__hover-dot'
                      />,
                    ];
              })}
            </>
          )}
        </Group>
        {xTicks.map((tick) => (
          <text
            key={tick.at}
            className='gxp-profile__axis-label'
            x={xScale(tick.at)}
            y={height - 6}
            textAnchor={tick.anchor}
          >
            {tick.label}
          </text>
        ))}
      </svg>
      {hover && (
        <GxpChartTooltip
          hover={hover}
          lines={model.lines}
          devices={devices}
          sensorLimits={sensorLimits}
        />
      )}
    </>
  );
};

const TemperatureProfileChart: React.FC<TemperatureProfileChartProps> = ({
  chartApiData,
  excursionEvents = NO_EXCURSIONS,
}) => {
  const profile = useMemo(
    () => temperatureProfile(chartApiData, excursionEvents),
    [chartApiData, excursionEvents]
  );
  const { parentRef, width } = useParentSize({
    debounceTime: 50,
    ignoreDimensions: ['height', 'top', 'left'],
  });

  if (profile.model.lines.length === 0) {
    return <div className='gxp-empty-text'>No temperature data available.</div>;
  }

  return (
    <div ref={parentRef} className='gxp-chart'>
      {width > 0 && <SizedChart profile={profile} width={width} />}
    </div>
  );
};

export default TemperatureProfileChart;
